import React, { useState, useRef, useEffect } from 'react';
import '../../styles/components/hero.css';
import VideoCacheManager from '../VideoCacheManager';

const Hero = ({ children }) => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const videoRef = useRef(null);
  const heroRef = useRef(null);

  useEffect(() => {
    // Use Intersection Observer to start loading video when hero section is about to be visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoadVideo(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '200px' } // Start loading 200px before the element is visible
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoadVideo) return;

    const video = videoRef.current;
    if (!video) return;

    const handleLoadedData = () => {
      setVideoLoaded(true);
    };

    const handleError = () => {
      setVideoError(true);
      console.warn('Video failed to load, falling back to static image');
    };

    const handleCanPlay = () => {
      // Ensure smooth playback by waiting for enough data
      if (video.readyState >= 3) {
        setVideoLoaded(true);
      }
    };

    const handleLoadStart = () => {
      console.log('Video loading started');
    };

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);
    video.addEventListener('loadstart', handleLoadStart);

    // Start loading the video
    video.load();

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
      video.removeEventListener('loadstart', handleLoadStart);
    };
  }, [shouldLoadVideo]);

  return (
    <section ref={heroRef} className="home-hero">
      {/* Video Cache Manager for better performance */}
      <VideoCacheManager />
      
      {shouldLoadVideo && (
        <video
          ref={videoRef}
          loop
          muted
          autoPlay
          playsInline
          preload="metadata"
          className={`home-video ${videoLoaded ? 'video-loaded' : 'video-loading'}`}
          onLoadedData={() => setVideoLoaded(true)}
          onError={() => setVideoError(true)}
        >
          {/* Provide multiple video sources for better compatibility and performance */}
          <source src="/play_claww.mp4" type="video/mp4" />
          <source src="/play_claww.webm" type="video/webm" />
          Your browser does not support the video tag.
        </video>
      )}

      {/* Loading overlay - show when video is loading */}
      {shouldLoadVideo && !videoLoaded && !videoError && (
        <div className="video-loading-overlay">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading video...</p>
        </div>
      )}

      {/* Fallback background for when video hasn't started loading
          (soft gradient defined in hero.css, matching the loading overlay) */}
      {!shouldLoadVideo && <div className="hero-fallback-bg" />}

      {children}
      <div className="home-hero-content">
        <div className="home-header-container1">
          <h1 className="home-heading10">
            Adding fun to your life!
          </h1>
          <p className="home-caption10">
            The claw machine arcade where everyone wins 💖
          </p>
        </div>
        <a
          href="https://www.google.com/maps/search/Lucky+Claww"
          target="_blank"
          rel="noopener noreferrer"
          className="home-button1"
        >
          Get started today
        </a>
      </div>
    </section>
  );
};

export default Hero;
